import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CountItemAction,
  CountVarianceReason,
} from '@/api/endpoints/inventory-count-sessions'
import { inventoryCountSessionsApi } from '@/api/endpoints/inventory-count-sessions'
import { ApiError } from '@/api/types'

const currentKey = (restaurantId: number) => [
  'count-session-current',
  restaurantId,
]
const detailKey = (restaurantId: number, sessionId: string) => [
  'count-session-detail',
  restaurantId,
  sessionId,
]
const historyKey = (restaurantId: number) => [
  'count-session-history',
  restaurantId,
]

/**
 * The open session for the restaurant, or `null` if none is open (a 404 from the backend is
 * resolved to `null` here, not thrown) — this keeps "resume" and "start" mutually exclusive by
 * construction instead of racing a stale `data` against a freshly-set `error` (the round-2 UX fix
 * for the stale-data bug: aborting a session used to leave both cards on screen at once).
 */
export function useCurrentCountSession(restaurantId: number | null) {
  return useQuery({
    ...currentCountSessionQuery(restaurantId ?? 0),
    enabled: restaurantId != null,
  })
}

/** Shared with `useCountSessionGuard`, which fetches it fresh at the moment of booking. */
export function currentCountSessionQuery(restaurantId: number) {
  return {
    queryKey: currentKey(restaurantId),
    queryFn: async () => {
      try {
        return await inventoryCountSessionsApi.current(restaurantId)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }
    },
    retry: false,
  }
}

export function useCountSessionHistory(restaurantId: number | null) {
  return useQuery({
    queryKey: historyKey(restaurantId ?? 0),
    queryFn: () => inventoryCountSessionsApi.history(restaurantId as number),
    enabled: restaurantId != null,
  })
}

export function useCountSessionDetail(
  restaurantId: number | null,
  sessionId: string | null,
) {
  return useQuery({
    queryKey: detailKey(restaurantId ?? 0, sessionId ?? ''),
    queryFn: () =>
      inventoryCountSessionsApi.detail(
        restaurantId as number,
        sessionId as string,
      ),
    enabled: restaurantId != null && sessionId != null,
  })
}

export function useOpenCountSession(restaurantId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => inventoryCountSessionsApi.open(restaurantId as number),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: currentKey(restaurantId ?? 0),
      })
    },
  })
}

export function useAbortCountSession(
  restaurantId: number | null,
  sessionId: string | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      inventoryCountSessionsApi.abort(
        restaurantId as number,
        sessionId as string,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: currentKey(restaurantId ?? 0),
      })
      queryClient.invalidateQueries({
        queryKey: historyKey(restaurantId ?? 0),
      })
    },
  })
}

export function useCloseCountSession(
  restaurantId: number | null,
  sessionId: string | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      inventoryCountSessionsApi.close(
        restaurantId as number,
        sessionId as string,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: currentKey(restaurantId ?? 0),
      })
      queryClient.invalidateQueries({
        queryKey: historyKey(restaurantId ?? 0),
      })
    },
  })
}

/**
 * `captureId` is the idempotency key: the same one on every resend of a capture, a fresh one for
 * every new capture. Sending a known id changes nothing and returns the item as it stands, which
 * is what makes a retry after a timeout safe on the `add` path, where a repeat would otherwise
 * accumulate a second time.
 */
export function useCaptureCountItem(
  restaurantId: number | null,
  sessionId: string | null,
) {
  return useMutation({
    mutationFn: (body: {
      sku: string
      qty: number
      mode?: 'add' | 'set'
      captureId?: string
    }) =>
      inventoryCountSessionsApi.capture(
        restaurantId as number,
        sessionId as string,
        body,
      ),
  })
}

export function useDeleteCountCapture(
  restaurantId: number | null,
  sessionId: string | null,
) {
  return useMutation({
    mutationFn: (sku: string) =>
      inventoryCountSessionsApi.deleteCapture(
        restaurantId as number,
        sessionId as string,
        sku,
      ),
  })
}

export function useUnknownScan(
  restaurantId: number | null,
  sessionId: string | null,
) {
  return useMutation({
    mutationFn: (body: { code: string; qty: number }) =>
      inventoryCountSessionsApi.unknownScan(
        restaurantId as number,
        sessionId as string,
        body,
      ),
  })
}

export function useReviewCountSession(
  restaurantId: number | null,
  sessionId: string | null,
) {
  return useMutation({
    mutationFn: () =>
      inventoryCountSessionsApi.review(
        restaurantId as number,
        sessionId as string,
      ),
  })
}

/**
 * One request for "ignore all remaining not-counted articles", replacing the review screen's
 * fan-out of one resolve per article. Resolves to how many rows the server settled.
 */
export function useSkipRemainingCountItems(
  restaurantId: number | null,
  sessionId: string | null,
) {
  return useMutation({
    mutationFn: () =>
      inventoryCountSessionsApi.skipRemaining(
        restaurantId as number,
        sessionId as string,
      ),
  })
}

export function useResolveCountItem(
  restaurantId: number | null,
  sessionId: string | null,
) {
  return useMutation({
    mutationFn: ({
      sku,
      action,
      reason,
      recountQty,
      recountConfirmed,
    }: {
      sku: string
      action: CountItemAction
      reason?: CountVarianceReason
      recountQty?: number
      recountConfirmed?: boolean
    }) =>
      inventoryCountSessionsApi.resolveItem(
        restaurantId as number,
        sessionId as string,
        sku,
        { action, reason, recountQty, recountConfirmed },
      ),
  })
}
